import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxNotifyService } from '@shared/services/ngx-notify/ngx-notify.service';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmDialogData } from '@shared/models/confirm-dialog-data';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { ResearcherAddComponent } from '../researcher-add/researcher-add.component';
import { ResearcherEditComponent } from '../researcher-edit/researcher-edit.component';
import { ResearcherService } from '../../services/researcher.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';
import { RESEARCHER_ROLES, RESEARCHER_ROLE_VALUES } from '@features/admin/researcher-management/models/researcher-roles.constants';

@Component({
  selector: 'app-researcher-management',
  templateUrl: './researcher-management.component.html',
  styleUrls: ['./researcher-management.component.scss']
})
export class ResearcherManagementComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  private destroyed$ = new Subject<void>();
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;

  dataSource!: MatTableDataSource<Researcher>;
  displayedColumns = ['lastName', 'firstName', 'email', 'phone', 'role', 'isActive', 'actions']; // ✅ Added 'role'
  
  isLoading = false;
  excelLoading = false; // for Excel export

  // Available roles for dropdown filter
  availableRoles = [
    { value: '', label: 'All Users' },
    ...RESEARCHER_ROLES
  ];

  constructor(
    public researcherService: ResearcherService,
    private notify: NgxNotifyService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<Researcher>([]);
    
    // Restore state from service
    this.sort.active = this.researcherService.sortActive;
    this.sort.direction = this.researcherService.sortDirection;
    this.paginator.pageIndex = this.researcherService.pageIndex;
    this.paginator.pageSize = this.researcherService.pageSize;
    
    this.loadResearchers();
  }

  loadResearchers(): void {
    this.isLoading = true;
    this.researcherService.getAll()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data) => {
          this.dataSource.data = data;
          this.isLoading = false;
        },
        (error: any) => {
          this.notify.error('Failed to load Researchers');
          this.isLoading = false;
        }
      );
  }

  resetFilter(): void {
    this.researcherService.resetFilter();
    this.paginator.pageIndex = 0;
    this.loadResearchers();
  }

  onSubmit(): void {
    this.researcherService.pageIndex = 0;
    this.paginator.pageIndex = 0;
    this.loadResearchers();
  }

  sortData(sort: Sort): void {
    this.researcherService.sortActive = sort.active;
    this.researcherService.sortDirection = sort.direction as 'asc' | 'desc';
    this.researcherService.pageIndex = 0;
    this.paginator.pageIndex = 0;
    this.loadResearchers();
  }

  onPageChange(event: PageEvent): void {
    this.researcherService.pageSize = event.pageSize;
    this.researcherService.pageIndex = event.pageIndex;
    this.loadResearchers();
  }

  // Add Researcher (no pre-selected role)
  addResearcher(): void {
    const dialogRef = this.dialog.open(ResearcherAddComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadResearchers();
      }
    });
  }

  edit(researcher: Researcher): void {

    this.dialog.open(ResearcherEditComponent, { data: researcher }).afterClosed().subscribe(result => {
      if (result) {
        this.loadResearchers();
      }
    });
  }

  remove(researcher: Researcher): void {
    const fullName = `${researcher.firstName} ${researcher.lastName}`;

    const data: ConfirmDialogData = {
      title: 'Remove User',
      description: `Are you sure you would like to remove ${fullName}?`
    };

    this.dialog.open(ConfirmationDialogComponent, { data }).afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        if (res) {
          this.deleteResearcher(researcher);
        }
      });
  }

   activate(researcher: Researcher): void {
    const fullName = `${researcher.firstName} ${researcher.lastName}`;
    const data: ConfirmDialogData = {
      title: 'Activate User',
      description: `Are you sure you would like to activate ${fullName}?`
    };

    this.dialog.open(ConfirmationDialogComponent, { data }).afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        if (res) {
          this.updateResearcher(researcher);
        }
      });
  }

  private updateResearcher(researcher: Researcher): void {

    const researcherDto: any = {
      id: researcher.id,
      firstName: researcher.firstName,
      lastName: researcher.lastName,
      email: researcher.email || null,
      phone: researcher.phone || null,
      isActive: true,
      roles: []
    };
    this.isLoading = true;
    
    this.researcherService.update(researcherDto)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        () => {
          this.notify.success('User Activated successfully');
          this.loadResearchers();
          this.isLoading = false;
        },
        (error: any) => {
          this.notify.error('Failed to activate User');
          this.isLoading = false;
        }
      );
  }

  private deleteResearcher(researcher: Researcher): void {
    this.isLoading = true;
    
    this.researcherService.delete(researcher.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        () => {
          this.notify.success('User deleted successfully');
          this.loadResearchers();
          this.isLoading = false;
        },
        (error: any) => {
          this.notify.error('Failed to delete User');
          this.isLoading = false;
        }
      );
  }

  formatPhone(phone: string): string {
    if (!phone) return '';
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  }

  // Format role value to display label
  formatRoleName(role: string): string {
    const roleMap: { [key: string]: string } = {
      'PrincipalInvestigator': 'Principal Investigator',
      'NTRIRBCoordinator': 'NTRIRB Coordinator',
      'ResearchPersonnel': 'Research Personnel'
    };
    return roleMap[role] || role;
  }

  hasActiveRole(element: any): boolean {
    console.log(element);
    return element.researcherRoles?.some((role: any) => role.isActive) ?? false;
  }

  downloadExcel() {
    this.excelLoading = true;
    this.researcherService.downloadReport().pipe(takeUntil(this.destroyed$)).subscribe({
      next: (blob) => {
        this.excelLoading = false;
        const date = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const fileName = `IRB_Users_${pad(date.getMonth() + 1)}_${pad(date.getDate())}_${date.getFullYear()}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => (this.excelLoading = false)
    });
 }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
